import type { JsonSchemaType, Object as PortalObject, User, UserGroup } from '@gmn-portal/backend';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Activity,
  Box,
  Database,
  Edit3,
  Eye,
  FileText,
  Link2,
  ShieldCheck,
  Users,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import type { DeepPartial } from 'ts-essentials';
import { z } from 'zod';
import {
  payloadClient,
  useCreateObject,
  useCustomer,
  useEntityPermissionOverview,
  useObject,
  useObjectCategories,
  useObjectCategory,
  useObjects,
  useUpdateObject,
  useUserGroups,
} from '@/core/api';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import {
  EntityPermissionsTab,
  type PermissionCustomerContext,
  ViewAsDialog,
} from '@/features/shared/permissions';
import { MultiSearchCombobox, SearchCombobox } from '@/shared/components/inputs';
import {
  DetailPageLayout,
  EntityReferenceList,
  EntityReferenceValue,
  TabNav,
} from '@/shared/components/layout';
import { MetadataAutoForm } from '@/shared/components/metadata/metadata-auto-form';
import { MetadataBuilderWithPreview } from '@/shared/components/metadata/metadata-builder/metadata-builder-with-preview';
import { MetadataReadView } from '@/shared/components/metadata/metadata-read-view';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@/shared/components/ui';
import { useUnsavedChangesGuard } from '@/shared/hooks/use-unsaved-changes-guard';
import { useViewAsState } from '@/shared/hooks/use-view-as';
import type { StatusOption, StatusValue } from '@/shared/schema';
import {
  getCustomerDetailHref,
  getObjectCategoryDetailHref,
  getObjectDetailHref,
  pickZodObjectFields,
  scrollToTarget,
} from '@/shared/utils';
import {
  createEmptyMetadataSchema,
  createMetadataZodSchema,
  getFieldPermission,
  parseMetadata,
  sanitizeMetadataValues,
} from '@/shared/utils/metadata';
import { getObjectCategoryIcon } from '@/shared/utils/object-category-icons';
import {
  canReadService,
  HELI_PROVIDER_GROUP_KEY,
  resolveUserGroupDomain,
} from '@/shared/utils/permissions';
import { ObjectActivity } from '../components/object-activity';
import { ObjectFiles } from '../components/object-files';
import { ObjectMetadataSidemenu } from '../components/object-metadata-sidemenu';
import { useScrollSpy } from '../components/use-scroll-spy';
import { createObjectSchema } from '../schema';
import type { ObjectFormValues, Object as PortalViewObject } from '../types';

const ensureStatusOptionsWithIds = (statusOptions?: StatusOption[]) => {
  if (!statusOptions) return undefined;

  return statusOptions.map((option, index) => ({
    ...option,
    id: option.id?.trim() || `status-opt-${option.color}-${index + 1}`,
  }));
};

const HELICOPTER_CATEGORY_NAMES = ['helikopter', 'rettungshubschrauber'];
const OFFSHORE_CATEGORY_NAMES = ['windpark', 'plattform'];
const SHIP_CATEGORY_NAMES = ['schiff', 'jack-up-schiff', 'jack up schiff', 'jack-up'];

const normalizeRelationshipIds = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => {
      if (typeof entry === 'string') return entry;
      if (entry && typeof entry === 'object' && 'id' in entry) {
        const id = (entry as { id?: unknown }).id;
        return typeof id === 'string' ? id : null;
      }
      return null;
    })
    .filter((entry): entry is string => Boolean(entry));
};

const haveSameRelationshipIds = (left: string[], right: string[]) => {
  if (left.length !== right.length) return false;

  const leftSorted = [...left].sort();
  const rightSorted = [...right].sort();
  return leftSorted.every((value, index) => value === rightSorted[index]);
};

export function ManageObject() {
  const { t, locale } = useIntl();
  const { user: currentUser } = useAuth();
  const createObject = useCreateObject();
  const updateObject = useUpdateObject();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const copyFromId = searchParams.get('copy');
  const categoryFromQuery = searchParams.get('category');
  const isEditMode = !!id;
  const isCopyMode = !!copyFromId;
  const [viewMode, setViewMode] = useState<'read' | 'edit'>(() => (isEditMode ? 'read' : 'edit'));
  const [scrollRoot, setScrollRoot] = useState<HTMLElement | null>(null);
  const [isViewAsDialogOpen, setIsViewAsDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Use regular or customer view object hooks based on mode
  const { data: object, isLoading: isLoadingObject } = useObject(id || '');
  const { data: objectToCopy } = useObject(copyFromId || '', {
    disabled: !copyFromId,
  });
  const { data: userGroupsData } = useUserGroups({ limit: 0, sort: 'key' });
  const userGroups = userGroupsData?.docs ?? [];
  const userGroupsById = useMemo(
    () => new Map(userGroups.map((group) => [group.id, group as UserGroup])),
    [userGroups]
  );
  const currentObject = object;

  const resolveGroupFromUser = useCallback(
    (user: User | null | undefined): UserGroup | null => {
      if (!user?.userGroup) return null;
      if (typeof user.userGroup === 'string') {
        return userGroupsById.get(user.userGroup) ?? null;
      }
      return user.userGroup as UserGroup;
    },
    [userGroupsById]
  );

  const currentGroup = resolveGroupFromUser(currentUser);
  const isCurrentGmnDomain = resolveUserGroupDomain(currentUser) === 'gmn';
  const isCurrentGmnAdmin = currentGroup?.key === 'gmn-admin';
  const canUseViewAs = isCurrentGmnAdmin && isEditMode;

  const { viewAsUser, viewAsAccess, isViewAsActive, setViewAsUserId, clearViewAs } = useViewAsState(
    {
      entityType: 'objects',
      entityId: isEditMode ? id : null,
      enabled: canUseViewAs,
    }
  );

  const effectiveUser = viewAsUser ?? currentUser;
  const effectiveGroup = resolveGroupFromUser(effectiveUser);
  const effectiveGroupKey = effectiveGroup?.key ?? null;
  const effectiveDomain = effectiveGroup?.domain ?? null;
  const isEffectiveGmnAdmin = effectiveGroupKey === 'gmn-admin';
  const isEffectiveCustomerAdmin = effectiveGroupKey === 'customer-admin';
  const isGmnUser = effectiveDomain === 'gmn';
  const isCustomer = effectiveDomain === 'customer';
  const canReadObjects = effectiveGroup ? canReadService(effectiveGroup, 'objects') : false;
  const canReadCustomers = effectiveGroup ? canReadService(effectiveGroup, 'customers') : false;

  useEffect(() => {
    if (typeof document === 'undefined') return;
    setScrollRoot(document.querySelector('main.overflow-y-auto') as HTMLElement | null);
  }, []);

  const assignedCustomerId =
    typeof currentObject?.assignedCustomer === 'object'
      ? currentObject?.assignedCustomer?.id
      : (currentObject?.assignedCustomer as string | undefined);
  const assignedHelicopterServiceProviderId =
    typeof currentObject?.assignedHelicopterServiceProvider === 'object'
      ? currentObject?.assignedHelicopterServiceProvider?.id
      : (currentObject?.assignedHelicopterServiceProvider as string | undefined);

  // Determine if customer can update this object
  let objectCustomerIdForAccess: string | undefined;
  if (currentObject) {
    objectCustomerIdForAccess =
      effectiveGroupKey === HELI_PROVIDER_GROUP_KEY
        ? assignedHelicopterServiceProviderId
        : assignedCustomerId;
  }

  const permissionCustomerContexts = useMemo<PermissionCustomerContext[]>(() => {
    const contexts: PermissionCustomerContext[] = [];

    if (assignedCustomerId) {
      contexts.push({
        customerId: assignedCustomerId,
        source: 'assigned-customer',
      });
    }

    if (assignedHelicopterServiceProviderId) {
      contexts.push({
        customerId: assignedHelicopterServiceProviderId,
        source: 'assigned-helicopter-service-provider',
      });
    }

    const contextByCustomerId = new Map<string, PermissionCustomerContext>();

    for (const context of contexts) {
      const existing = contextByCustomerId.get(context.customerId);
      if (!existing) {
        contextByCustomerId.set(context.customerId, context);
        continue;
      }

      if (existing.source !== context.source) {
        contextByCustomerId.set(context.customerId, {
          customerId: context.customerId,
          source: 'combined',
        });
      }
    }

    return Array.from(contextByCustomerId.values());
  }, [assignedCustomerId, assignedHelicopterServiceProviderId]);

  const prioritizedPermissionCustomerContexts = useMemo(() => {
    if (!objectCustomerIdForAccess) return permissionCustomerContexts;

    const prioritized = permissionCustomerContexts.filter(
      (context) => context.customerId === objectCustomerIdForAccess
    );
    const remaining = permissionCustomerContexts.filter(
      (context) => context.customerId !== objectCustomerIdForAccess
    );

    return [...prioritized, ...remaining];
  }, [objectCustomerIdForAccess, permissionCustomerContexts]);

  const effectiveUserCustomerId =
    typeof effectiveUser?.customer === 'object'
      ? effectiveUser?.customer?.id
      : (effectiveUser?.customer as string | undefined);

  const {
    data: currentAccessOverview,
    isLoading: isLoadingCurrentAccessOverview,
    isFetching: isFetchingCurrentAccessOverview,
  } = useEntityPermissionOverview({
    entityType: 'objects',
    entityId: id ?? '',
    userIds: currentUser?.id ? [currentUser.id] : [],
    disabled: !isEditMode || !id || effectiveDomain !== 'customer' || isViewAsActive,
  });
  const currentAccessEntry = currentAccessOverview?.entries?.[0] ?? null;

  const canUpdateObject =
    !isLoadingObject &&
    (isEffectiveGmnAdmin ||
      (isCustomer &&
        !isViewAsActive &&
        !isLoadingCurrentAccessOverview &&
        !isFetchingCurrentAccessOverview &&
        Boolean(currentAccessEntry?.update.allowed)));

  const viewAsUpdateAllowed = Boolean(viewAsAccess?.update.allowed);
  const canPreviewEditObject = isViewAsActive ? viewAsUpdateAllowed : canUpdateObject;
  const canSaveObject = !isViewAsActive && canPreviewEditObject;
  const formDisabled = isLoadingObject || !canPreviewEditObject;

  const assignedCustomerIdForRead = assignedCustomerId;
  const { data: assignedCustomerForRead } = useCustomer(assignedCustomerIdForRead ?? '');

  const assignedHeliProviderIdForRead = assignedHelicopterServiceProviderId;
  const { data: assignedHeliProviderForRead } = useCustomer(assignedHeliProviderIdForRead ?? '');

  // Determine the appropriate back href based on context
  const backHrefBase = isCurrentGmnDomain ? '/gmn/objects' : '/c/objects';
  const backHref = `${backHrefBase}${categoryFromQuery ? `?category=${categoryFromQuery}` : ''}`;

  const [isMetadataDrawerOpen, setIsMetadataDrawerOpen] = useState(false);
  const [categoryMetadataZodSchema, setCategoryMetadataZodSchema] = useState<
    z.ZodObject<z.ZodRawShape>
  >(z.object({}));
  const [dynamicMetadataZodSchema, setDynamicMetadataZodSchema] = useState<
    z.ZodObject<z.ZodRawShape>
  >(z.object({}));
  const [drawerSchema, setDrawerSchema] = useState<JsonSchemaType | undefined>(undefined);

  const initialValuesRef = useRef<ObjectFormValues | null>(null);
  const previousHeliProviderIdRef = useRef<string | null>(null);
  const previousOffshoreProviderIdRef = useRef<string | null>(null);
  const offshoreLinksHydratedForObjectRef = useRef<string | null>(null);
  const [linkedHelicopterIdsForOffshoreEdit, setLinkedHelicopterIdsForOffshoreEdit] = useState<
    string[]
  >([]);
  const [initialLinkedHelicopterIdsForOffshoreEdit, setInitialLinkedHelicopterIdsForOffshoreEdit] =
    useState<string[]>([]);
  const windparkLinkedShipsHydratedForObjectRef = useRef<string | null>(null);
  const [linkedShipIdsForWindparkEdit, setLinkedShipIdsForWindparkEdit] = useState<string[]>([]);
  const [initialLinkedShipIdsForWindparkEdit, setInitialLinkedShipIdsForWindparkEdit] = useState<
    string[]
  >([]);
  const linkedHelicopterIdsForOffshoreEditRef = useRef<string[]>([]);
  const initialLinkedHelicopterIdsForOffshoreEditRef = useRef<string[]>([]);
  const linkedShipIdsForWindparkEditRef = useRef<string[]>([]);
  const initialLinkedShipIdsForWindparkEditRef = useRef<string[]>([]);

  const updateLinkedHelicopterIdsForOffshoreEdit = useCallback((nextIds: string[]) => {
    linkedHelicopterIdsForOffshoreEditRef.current = nextIds;
    setLinkedHelicopterIdsForOffshoreEdit(nextIds);
  }, []);

  const updateInitialLinkedHelicopterIdsForOffshoreEdit = useCallback((nextIds: string[]) => {
    initialLinkedHelicopterIdsForOffshoreEditRef.current = nextIds;
    setInitialLinkedHelicopterIdsForOffshoreEdit(nextIds);
  }, []);

  const updateLinkedShipIdsForWindparkEdit = useCallback((nextIds: string[]) => {
    linkedShipIdsForWindparkEditRef.current = nextIds;
    setLinkedShipIdsForWindparkEdit(nextIds);
  }, []);

  const updateInitialLinkedShipIdsForWindparkEdit = useCallback((nextIds: string[]) => {
    initialLinkedShipIdsForWindparkEditRef.current = nextIds;
    setInitialLinkedShipIdsForWindparkEdit(nextIds);
  }, []);

  const initialCategory = !isEditMode && !isCopyMode && categoryFromQuery ? categoryFromQuery : '';
  const form = useForm<ObjectFormValues, any, ObjectFormValues>({
    resolver: zodResolver(
      createObjectSchema(t, categoryMetadataZodSchema, dynamicMetadataZodSchema)
    ),
    defaultValues: {
      name: '',
      description: '',
      category: initialCategory,
      assignedCustomer: '',
      assignedHelicopterServiceProvider: '',
      linkedWindpark: null,
      linkedOffshoreObjects: [],
      dynamicMetadataSchema: createEmptyMetadataSchema(),
      categoryMetadataValues: {},
      dynamicMetadataValues: {},
    },
  });

  useEffect(() => {
    setViewMode(isEditMode ? 'read' : 'edit');
  }, [isEditMode, id]);

  // Track selected category ID for fetching its schema
  const selectedCategoryId = form.watch('category') || '';
  const { data: selectedCategory } = useObjectCategory(selectedCategoryId, {
    disabled: !selectedCategoryId,
  });
  const selectedCategoryName = selectedCategory?.name?.toLocaleLowerCase('de-DE') ?? null;
  const isHelicopterCategory = HELICOPTER_CATEGORY_NAMES.includes(selectedCategoryName || '');
  const isOffshoreCategory = OFFSHORE_CATEGORY_NAMES.includes(selectedCategoryName || '');
  const isShipCategory = SHIP_CATEGORY_NAMES.includes(selectedCategoryName || '');
  const isWindparkCategory = selectedCategoryName === 'windpark';

  const selectedHeliProviderIdRaw = form.watch('assignedHelicopterServiceProvider');
  const selectedHeliProviderId =
    typeof selectedHeliProviderIdRaw === 'string' ? selectedHeliProviderIdRaw : '';

  const { data: helicopterCategoryOptions } = useObjectCategories({
    where: {
      or: [{ name: { equals: 'Helikopter' } }, { name: { equals: 'Rettungshubschrauber' } }],
    },
    limit: 20,
    disabled: !isHelicopterCategory && !isOffshoreCategory,
  });
  const helicopterCategoryIds = useMemo(
    () => (helicopterCategoryOptions?.docs || []).map((category) => String(category.id)),
    [helicopterCategoryOptions?.docs]
  );

  const { data: offshoreCategoryOptions } = useObjectCategories({
    where: {
      or: [{ name: { equals: 'Windpark' } }, { name: { equals: 'Plattform' } }],
    },
    limit: 20,
    disabled: !isHelicopterCategory,
  });
  const offshoreCategoryIds = useMemo(
    () => (offshoreCategoryOptions?.docs || []).map((category) => String(category.id)),
    [offshoreCategoryOptions?.docs]
  );

  const { data: windparkCategoryOptions } = useObjectCategories({
    where: {
      name: { equals: 'Windpark' },
    },
    limit: 200,
    disabled: !isShipCategory,
  });
  const windparkCategoryIds = useMemo(
    () => (windparkCategoryOptions?.docs || []).map((category) => String(category.id)),
    [windparkCategoryOptions?.docs]
  );

  const { data: shipCategoryOptions } = useObjectCategories({
    where: {
      or: [
        { name: { equals: 'Schiff' } },
        { name: { equals: 'Jack-Up-Schiff' } },
        { name: { equals: 'Jack Up Schiff' } },
        { name: { equals: 'Jack-Up' } },
      ],
    },
    limit: 200,
    disabled: !isShipCategory && !isWindparkCategory,
  });
  const shipCategoryIds = useMemo(
    () => (shipCategoryOptions?.docs || []).map((category) => String(category.id)),
    [shipCategoryOptions?.docs]
  );

  useEffect(() => {
    if (isHelicopterCategory) return;
    const currentLinks = (form.getValues('linkedOffshoreObjects') || []) as string[];
    if (currentLinks.length === 0) return;
    form.setValue('linkedOffshoreObjects', [], { shouldDirty: true, shouldValidate: false });
  }, [form, isHelicopterCategory]);

  useEffect(() => {
    if (isShipCategory) return;
    if (!form.getValues('linkedWindpark')) return;
    form.setValue('linkedWindpark', null, { shouldDirty: true, shouldValidate: false });
  }, [form, isShipCategory]);

  useEffect(() => {
    if (isWindparkCategory) return;
    windparkLinkedShipsHydratedForObjectRef.current = null;
    if (
      linkedShipIdsForWindparkEdit.length === 0 &&
      initialLinkedShipIdsForWindparkEdit.length === 0
    ) {
      return;
    }
    updateLinkedShipIdsForWindparkEdit([]);
    updateInitialLinkedShipIdsForWindparkEdit([]);
  }, [
    initialLinkedShipIdsForWindparkEdit.length,
    isWindparkCategory,
    linkedShipIdsForWindparkEdit.length,
    updateInitialLinkedShipIdsForWindparkEdit,
    updateLinkedShipIdsForWindparkEdit,
  ]);

  useEffect(() => {
    if (!isHelicopterCategory) {
      previousHeliProviderIdRef.current = null;
      return;
    }

    const nextHeliProviderId = selectedHeliProviderId || null;
    const previousHeliProviderId = previousHeliProviderIdRef.current;

    if (previousHeliProviderId !== null && previousHeliProviderId !== nextHeliProviderId) {
      const currentLinks = (form.getValues('linkedOffshoreObjects') || []) as string[];
      if (currentLinks.length > 0) {
        form.setValue('linkedOffshoreObjects', [], { shouldDirty: true, shouldValidate: false });
      }
    }

    previousHeliProviderIdRef.current = nextHeliProviderId;
  }, [form, isHelicopterCategory, selectedHeliProviderId]);

  useEffect(() => {
    if (!isOffshoreCategory) {
      previousOffshoreProviderIdRef.current = null;
      return;
    }

    const nextProviderId = selectedHeliProviderId || null;
    const previousProviderId = previousOffshoreProviderIdRef.current;

    if (previousProviderId !== null && previousProviderId !== nextProviderId) {
      if (linkedHelicopterIdsForOffshoreEdit.length > 0) {
        updateLinkedHelicopterIdsForOffshoreEdit([]);
      }
    }

    previousOffshoreProviderIdRef.current = nextProviderId;
  }, [
    isOffshoreCategory,
    linkedHelicopterIdsForOffshoreEdit.length,
    selectedHeliProviderId,
    updateLinkedHelicopterIdsForOffshoreEdit,
  ]);

  const categoryMetadataSchema = selectedCategory?.metadataSchema as JsonSchemaType | undefined;

  const resetFormFromObject = useCallback(
    (obj: PortalObject, opts?: { nameOverride?: string }) => {
      if (!obj) return;
      const assignedCustomerId =
        typeof obj.assignedCustomer === 'object' ? obj.assignedCustomer?.id : obj.assignedCustomer;
      const assignedHeliProviderId =
        typeof obj.assignedHelicopterServiceProvider === 'object'
          ? obj.assignedHelicopterServiceProvider?.id
          : obj.assignedHelicopterServiceProvider;

      const categoryId = typeof obj.category === 'object' ? obj.category?.id : obj.category;

      const nextValues: ObjectFormValues = {
        name: opts?.nameOverride ?? obj.name,
        description: obj.description || '',
        category: categoryId || '',
        assignedCustomer: assignedCustomerId || '',
        assignedHelicopterServiceProvider: assignedHeliProviderId ?? null,
        linkedWindpark:
          (typeof obj.linkedWindpark === 'object' ? obj.linkedWindpark?.id : obj.linkedWindpark) ??
          null,
        linkedOffshoreObjects: Array.isArray(obj.linkedOffshoreObjects)
          ? obj.linkedOffshoreObjects
              .map((entry) => (typeof entry === 'string' ? entry : entry?.id || null))
              .filter((entry): entry is string => Boolean(entry))
          : [],
        dynamicMetadataSchema:
          (obj.dynamicMetadataSchema as JsonSchemaType) || createEmptyMetadataSchema(),
        categoryMetadataValues: parseMetadata(
          obj.categoryMetadataValues as Record<string, unknown> | undefined
        ),
        dynamicMetadataValues: parseMetadata(
          obj.dynamicMetadataValues as Record<string, unknown> | undefined
        ),
      };

      previousHeliProviderIdRef.current =
        typeof assignedHeliProviderId === 'string' ? assignedHeliProviderId : null;
      previousOffshoreProviderIdRef.current =
        typeof assignedHeliProviderId === 'string' ? assignedHeliProviderId : null;
      form.reset(nextValues);
      initialValuesRef.current = nextValues;
    },
    [form]
  );

  useEffect(() => {
    if (isEditMode && object) {
      resetFormFromObject(object);
    } else if (isCopyMode && objectToCopy) {
      resetFormFromObject(objectToCopy, {
        nameOverride: `${objectToCopy.name} (${t('common.copy')})`,
      });
    }
  }, [isEditMode, isCopyMode, object, objectToCopy, resetFormFromObject, t]);

  useEffect(() => {
    if (isEditMode || isCopyMode) return;
    if (!initialValuesRef.current) {
      initialValuesRef.current = form.getValues();
    }
  }, [form, isCopyMode, isEditMode]);

  // Update category metadata zod schema when category changes
  useEffect(() => {
    if (categoryMetadataSchema) {
      setCategoryMetadataZodSchema(
        createMetadataZodSchema(categoryMetadataSchema, effectiveGroupKey)
      );
    } else {
      setCategoryMetadataZodSchema(z.object({}));
    }
  }, [categoryMetadataSchema, effectiveGroupKey]);

  // Update dynamic metadata zod schema when object schema changes
  useEffect(() => {
    const objectSchema = form.watch('dynamicMetadataSchema') as JsonSchemaType | undefined;
    if (objectSchema && Object.keys(objectSchema.properties || {}).length > 0) {
      setDynamicMetadataZodSchema(createMetadataZodSchema(objectSchema, effectiveGroupKey));
    } else {
      setDynamicMetadataZodSchema(z.object({}));
    }
  }, [form.watch('dynamicMetadataSchema'), effectiveGroupKey]);

  // Sanitize category metadata values when category schema changes
  useEffect(() => {
    if (categoryMetadataSchema) {
      const currentValues = (form.getValues('categoryMetadataValues') || {}) as Record<
        string,
        unknown
      >;
      const sanitizedValues = sanitizeMetadataValues(currentValues, categoryMetadataSchema);
      if (JSON.stringify(currentValues) !== JSON.stringify(sanitizedValues)) {
        form.setValue('categoryMetadataValues', sanitizedValues, {
          shouldValidate: false,
        });
      }
    }
  }, [categoryMetadataSchema, form]);

  // Sanitize dynamic metadata values when object schema changes
  useEffect(() => {
    const objectSchema = form.getValues('dynamicMetadataSchema') as JsonSchemaType | undefined;
    if (objectSchema) {
      const currentValues = (form.getValues('dynamicMetadataValues') || {}) as Record<
        string,
        unknown
      >;
      const sanitizedValues = sanitizeMetadataValues(currentValues, objectSchema);
      if (JSON.stringify(currentValues) !== JSON.stringify(sanitizedValues)) {
        form.setValue('dynamicMetadataValues', sanitizedValues, {
          shouldValidate: false,
        });
      }
    }
  }, [form.watch('dynamicMetadataSchema'), form]);

  // Initialize drawer schema when opening
  const openMetadataDrawer = () => {
    setDrawerSchema(form.getValues().dynamicMetadataSchema);
    setIsMetadataDrawerOpen(true);
  };

  const handleMetadataBuilderSave = () => {
    if (drawerSchema) {
      form.setValue('dynamicMetadataSchema', drawerSchema);
      setDynamicMetadataZodSchema(createMetadataZodSchema(drawerSchema, effectiveGroupKey));
    }
    setIsMetadataDrawerOpen(false);
  };

  const discardChanges = useCallback(() => {
    if (isEditMode && currentObject) {
      resetFormFromObject(currentObject);
      setViewMode('read');
    } else if (isCopyMode && objectToCopy) {
      resetFormFromObject(objectToCopy, {
        nameOverride: `${objectToCopy.name} (${t('common.copy')})`,
      });
    } else if (initialValuesRef.current) {
      form.reset(initialValuesRef.current);
    }
    updateLinkedHelicopterIdsForOffshoreEdit(initialLinkedHelicopterIdsForOffshoreEdit);
    updateLinkedShipIdsForWindparkEdit(initialLinkedShipIdsForWindparkEdit);
    setDrawerSchema(undefined);
    setIsMetadataDrawerOpen(false);
  }, [
    currentObject,
    form,
    initialLinkedHelicopterIdsForOffshoreEdit,
    initialLinkedShipIdsForWindparkEdit,
    isCopyMode,
    isEditMode,
    objectToCopy,
    resetFormFromObject,
    t,
    updateLinkedHelicopterIdsForOffshoreEdit,
    updateLinkedShipIdsForWindparkEdit,
  ]);

  const linkedOffshoreIdsForRead = useMemo(() => {
    if (!Array.isArray(currentObject?.linkedOffshoreObjects)) return [];
    return currentObject.linkedOffshoreObjects
      .map((entry) => (typeof entry === 'string' ? entry : entry?.id || null))
      .filter((entry): entry is string => Boolean(entry));
  }, [currentObject?.linkedOffshoreObjects]);

  const linkedWindparkIdForRead = useMemo(
    () =>
      typeof currentObject?.linkedWindpark === 'object'
        ? currentObject?.linkedWindpark?.id
        : currentObject?.linkedWindpark,
    [currentObject?.linkedWindpark]
  );

  const { data: linkedWindparkForRead } = useObjects({
    where: linkedWindparkIdForRead
      ? {
          id: {
            equals: linkedWindparkIdForRead,
          },
        }
      : undefined,
    limit: 1,
    disabled: !linkedWindparkIdForRead,
  });

  const linkedWindparkReferenceItem = useMemo(() => {
    if (!linkedWindparkIdForRead) return null;
    const linkedWindpark = linkedWindparkForRead?.docs?.[0];
    return {
      id: linkedWindparkIdForRead,
      label: linkedWindpark?.name || linkedWindparkIdForRead,
      href: getObjectDetailHref({
        objectId: linkedWindparkIdForRead,
        domain: effectiveDomain,
        canReadObjects,
      }),
    };
  }, [canReadObjects, effectiveDomain, linkedWindparkForRead?.docs, linkedWindparkIdForRead]);

  const { data: linkedOffshoreObjectsForRead } = useObjects({
    where: {
      id: {
        in: linkedOffshoreIdsForRead,
      },
    },
    limit: linkedOffshoreIdsForRead.length || 1,
    disabled: linkedOffshoreIdsForRead.length === 0,
  });

  const linkedOffshoreReferenceItems = useMemo(() => {
    if (linkedOffshoreIdsForRead.length === 0) return [];
    const byId = new Map((linkedOffshoreObjectsForRead?.docs || []).map((doc) => [doc.id, doc]));
    return linkedOffshoreIdsForRead.map((linkedId) => {
      const linkedObject = byId.get(linkedId);
      return {
        id: linkedId,
        label: linkedObject?.name || linkedId,
        href: getObjectDetailHref({
          objectId: linkedId,
          domain: effectiveDomain,
          canReadObjects,
        }),
      };
    });
  }, [
    linkedOffshoreIdsForRead,
    linkedOffshoreObjectsForRead?.docs,
    effectiveDomain,
    canReadObjects,
  ]);

  const currentObjectId = currentObject?.id ?? id ?? '';
  const { data: linkedHelicoptersForOffshoreObject } = useObjects({
    where: {
      and: [
        {
          linkedOffshoreObjects: {
            contains: currentObjectId,
          },
        },
        {
          category: {
            in: helicopterCategoryIds,
          },
        },
      ],
    },
    limit: 100,
    disabled: !isOffshoreCategory || !currentObjectId || helicopterCategoryIds.length === 0,
  });

  const linkedHelicopterIdsForOffshoreRead = useMemo(
    () => (linkedHelicoptersForOffshoreObject?.docs || []).map((doc) => String(doc.id)),
    [linkedHelicoptersForOffshoreObject?.docs]
  );

  const linkedHelicopterReferenceItems = useMemo(
    () =>
      (linkedHelicoptersForOffshoreObject?.docs || []).map((doc) => ({
        id: String(doc.id),
        label: doc.name || String(doc.id),
        href: getObjectDetailHref({
          objectId: doc.id,
          domain: effectiveDomain,
          canReadObjects,
        }),
      })),
    [linkedHelicoptersForOffshoreObject?.docs, effectiveDomain, canReadObjects]
  );

  const { data: linkedShipsForWindparkObject } = useObjects({
    where: currentObjectId
      ? {
          and: [
            {
              linkedWindpark: {
                equals: currentObjectId,
              },
            },
            ...(shipCategoryIds.length > 0
              ? [
                  {
                    category: {
                      in: shipCategoryIds,
                    },
                  },
                ]
              : []),
          ],
        }
      : undefined,
    sort: 'name',
    limit: 200,
    disabled: !isWindparkCategory || !currentObjectId,
  });

  const linkedShipIdsForWindparkRead = useMemo(
    () => (linkedShipsForWindparkObject?.docs || []).map((doc) => String(doc.id)),
    [linkedShipsForWindparkObject?.docs]
  );

  const linkedShipReferenceItems = useMemo(
    () =>
      (linkedShipsForWindparkObject?.docs || []).map((doc) => ({
        id: String(doc.id),
        label: doc.name || String(doc.id),
        href: getObjectDetailHref({
          objectId: doc.id,
          domain: effectiveDomain,
          canReadObjects,
        }),
      })),
    [canReadObjects, effectiveDomain, linkedShipsForWindparkObject?.docs]
  );

  useEffect(() => {
    if (!isEditMode || !isOffshoreCategory || !currentObjectId || viewMode !== 'read') {
      return;
    }

    if (!linkedHelicoptersForOffshoreObject) return;
    if (offshoreLinksHydratedForObjectRef.current === currentObjectId) return;

    updateLinkedHelicopterIdsForOffshoreEdit(linkedHelicopterIdsForOffshoreRead);
    updateInitialLinkedHelicopterIdsForOffshoreEdit(linkedHelicopterIdsForOffshoreRead);
    offshoreLinksHydratedForObjectRef.current = currentObjectId;
  }, [
    currentObjectId,
    isEditMode,
    isOffshoreCategory,
    linkedHelicoptersForOffshoreObject,
    linkedHelicopterIdsForOffshoreRead,
    updateInitialLinkedHelicopterIdsForOffshoreEdit,
    updateLinkedHelicopterIdsForOffshoreEdit,
    viewMode,
  ]);

  useEffect(() => {
    if (!isEditMode || !isWindparkCategory || !currentObjectId || viewMode !== 'read') {
      return;
    }

    if (!linkedShipsForWindparkObject) return;
    if (windparkLinkedShipsHydratedForObjectRef.current === currentObjectId) return;

    updateLinkedShipIdsForWindparkEdit(linkedShipIdsForWindparkRead);
    updateInitialLinkedShipIdsForWindparkEdit(linkedShipIdsForWindparkRead);
    windparkLinkedShipsHydratedForObjectRef.current = currentObjectId;
  }, [
    currentObjectId,
    isEditMode,
    isWindparkCategory,
    linkedShipIdsForWindparkRead,
    linkedShipsForWindparkObject,
    updateInitialLinkedShipIdsForWindparkEdit,
    updateLinkedShipIdsForWindparkEdit,
    viewMode,
  ]);

  useEffect(() => {
    if (isOffshoreCategory) return;
    offshoreLinksHydratedForObjectRef.current = null;
    if (
      linkedHelicopterIdsForOffshoreEdit.length === 0 &&
      initialLinkedHelicopterIdsForOffshoreEdit.length === 0
    ) {
      return;
    }

    updateLinkedHelicopterIdsForOffshoreEdit([]);
    updateInitialLinkedHelicopterIdsForOffshoreEdit([]);
  }, [
    initialLinkedHelicopterIdsForOffshoreEdit.length,
    isOffshoreCategory,
    linkedHelicopterIdsForOffshoreEdit.length,
    updateInitialLinkedHelicopterIdsForOffshoreEdit,
    updateLinkedHelicopterIdsForOffshoreEdit,
  ]);

  const offshoreLinkedHelicoptersDirty = useMemo(() => {
    if (!isEditMode || !isOffshoreCategory || viewMode !== 'edit') return false;

    return !haveSameRelationshipIds(
      linkedHelicopterIdsForOffshoreEdit,
      initialLinkedHelicopterIdsForOffshoreEdit
    );
  }, [
    initialLinkedHelicopterIdsForOffshoreEdit,
    isEditMode,
    isOffshoreCategory,
    linkedHelicopterIdsForOffshoreEdit,
    viewMode,
  ]);

  const windparkLinkedShipsDirty = useMemo(() => {
    if (!isEditMode || !isWindparkCategory || viewMode !== 'edit') return false;

    return !haveSameRelationshipIds(
      linkedShipIdsForWindparkEdit,
      initialLinkedShipIdsForWindparkEdit
    );
  }, [
    initialLinkedShipIdsForWindparkEdit,
    isEditMode,
    isWindparkCategory,
    linkedShipIdsForWindparkEdit,
    viewMode,
  ]);

  const canEditOffshoreLinkedHelicopters =
    isOffshoreCategory && isEffectiveGmnAdmin && !isViewAsActive;
  const canEditWindparkLinkedShips = isWindparkCategory && isEffectiveGmnAdmin && !isViewAsActive;

  const { dialog: unsavedDialog, allowNextNavigation } = useUnsavedChangesGuard({
    when:
      (form.formState.isDirty || offshoreLinkedHelicoptersDirty || windparkLinkedShipsDirty) &&
      viewMode === 'edit' &&
      !isViewAsActive,
    onDiscard: discardChanges,
  });

  const previousViewAsRef = useRef(false);
  const previousViewAsUserRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isEditMode) return;

    if (isViewAsActive) {
      const currentViewAsUserId = viewAsUser?.id ?? null;
      const userChanged = previousViewAsUserRef.current !== currentViewAsUserId;
      if (!previousViewAsRef.current || userChanged) {
        discardChanges();
      }

      setViewMode(viewAsUpdateAllowed ? 'edit' : 'read');
      previousViewAsUserRef.current = currentViewAsUserId;
    } else if (previousViewAsRef.current) {
      setViewMode('read');
      previousViewAsUserRef.current = null;
    }

    previousViewAsRef.current = isViewAsActive;
  }, [discardChanges, isEditMode, isViewAsActive, viewAsUpdateAllowed, viewAsUser?.id]);

  useEffect(() => {
    if (!isViewAsActive || viewAsUpdateAllowed) return;
    if (
      viewMode !== 'read' ||
      form.formState.isDirty ||
      offshoreLinkedHelicoptersDirty ||
      windparkLinkedShipsDirty
    ) {
      discardChanges();
    }
  }, [
    discardChanges,
    form.formState.isDirty,
    isViewAsActive,
    offshoreLinkedHelicoptersDirty,
    windparkLinkedShipsDirty,
    viewAsUpdateAllowed,
    viewMode,
  ]);

  const syncHelicopterBackReferencesForOffshoreObject = useCallback(
    async (offshoreObjectId: string, nextHelicopterIds: string[]) => {
      const previousHelicopterIds = initialLinkedHelicopterIdsForOffshoreEditRef.current;
      const idsToLink = nextHelicopterIds.filter(
        (helicopterId) => !previousHelicopterIds.includes(helicopterId)
      );
      const idsToUnlink = previousHelicopterIds.filter(
        (helicopterId) => !nextHelicopterIds.includes(helicopterId)
      );

      const changedHelicopterIds = [...new Set([...idsToLink, ...idsToUnlink])];
      if (changedHelicopterIds.length === 0) return;

      for (const helicopterId of changedHelicopterIds) {
        const helicopter = await payloadClient.findById({
          collection: 'objects',
          id: helicopterId,
          depth: 0,
        });
        const currentLinks = normalizeRelationshipIds(helicopter.linkedOffshoreObjects);

        const nextLinks = idsToLink.includes(helicopterId)
          ? [...new Set([...currentLinks, offshoreObjectId])]
          : currentLinks.filter((linkedObjectId) => linkedObjectId !== offshoreObjectId);

        if (JSON.stringify(currentLinks) === JSON.stringify(nextLinks)) {
          continue;
        }

        await updateObject.mutateAsync({
          id: helicopterId,
          data: {
            linkedOffshoreObjects: nextLinks,
          },
        });
      }
    },
    [updateObject]
  );

  const syncShipBackReferencesForWindparkObject = useCallback(
    async (windparkObjectId: string, nextShipIds: string[]) => {
      const previousShipIds = initialLinkedShipIdsForWindparkEditRef.current;
      const idsToLink = nextShipIds.filter((shipId) => !previousShipIds.includes(shipId));
      const idsToUnlink = previousShipIds.filter((shipId) => !nextShipIds.includes(shipId));
      const changedShipIds = [...new Set([...idsToLink, ...idsToUnlink])];

      if (changedShipIds.length === 0) return;

      for (const shipId of changedShipIds) {
        const ship = await payloadClient.findById({
          collection: 'objects',
          id: shipId,
          depth: 0,
        });

        const currentLinkedWindparkId =
          typeof ship.linkedWindpark === 'object' ? ship.linkedWindpark?.id : ship.linkedWindpark;

        const nextLinkedWindparkId = idsToLink.includes(shipId)
          ? windparkObjectId
          : currentLinkedWindparkId === windparkObjectId
            ? null
            : (currentLinkedWindparkId ?? null);

        if ((currentLinkedWindparkId ?? null) === nextLinkedWindparkId) {
          continue;
        }

        await updateObject.mutateAsync({
          id: shipId,
          data: {
            linkedWindpark: nextLinkedWindparkId,
          },
        });
      }
    },
    [updateObject]
  );

  const handleSubmit = async (data: ObjectFormValues) => {
    if (!canSaveObject || isSaving) return;

    // Let relation combobox selections flush before snapshotting linked IDs for save.
    await new Promise<void>((resolve) => {
      window.setTimeout(resolve, 0);
    });

    const payloadData = {
      ...data,
      linkedWindpark: data.linkedWindpark || null,
    } as DeepPartial<PortalObject>;
    const latestLinkedHelicopterIdsForOffshoreEdit = linkedHelicopterIdsForOffshoreEditRef.current;
    const latestLinkedShipIdsForWindparkEdit = linkedShipIdsForWindparkEditRef.current;
    const shouldSyncOffshoreBackReferences =
      Boolean(id) &&
      canEditOffshoreLinkedHelicopters &&
      !haveSameRelationshipIds(
        latestLinkedHelicopterIdsForOffshoreEdit,
        initialLinkedHelicopterIdsForOffshoreEditRef.current
      );
    const shouldSyncWindparkBackReferences =
      Boolean(id) &&
      canEditWindparkLinkedShips &&
      !haveSameRelationshipIds(
        latestLinkedShipIdsForWindparkEdit,
        initialLinkedShipIdsForWindparkEditRef.current
      );

    try {
      setIsSaving(true);

      if (isEditMode) {
        if (form.formState.isDirty) {
          await updateObject.mutateAsync({ id, data: payloadData });
        }

        if (shouldSyncOffshoreBackReferences && id) {
          await syncHelicopterBackReferencesForOffshoreObject(
            id,
            latestLinkedHelicopterIdsForOffshoreEdit
          );
          updateInitialLinkedHelicopterIdsForOffshoreEdit(latestLinkedHelicopterIdsForOffshoreEdit);
          offshoreLinksHydratedForObjectRef.current = id;
        }

        if (shouldSyncWindparkBackReferences && id) {
          await syncShipBackReferencesForWindparkObject(id, latestLinkedShipIdsForWindparkEdit);
          updateInitialLinkedShipIdsForWindparkEdit(latestLinkedShipIdsForWindparkEdit);
          windparkLinkedShipsHydratedForObjectRef.current = id;
        }

        toast.success(t('objects.objectUpdatedSuccessfully'));
        setViewMode('read');
      } else {
        const createdObject = await createObject.mutateAsync(payloadData);
        if (
          canEditOffshoreLinkedHelicopters &&
          latestLinkedHelicopterIdsForOffshoreEdit.length > 0
        ) {
          await syncHelicopterBackReferencesForOffshoreObject(
            createdObject.doc.id,
            latestLinkedHelicopterIdsForOffshoreEdit
          );
        }
        if (canEditWindparkLinkedShips && latestLinkedShipIdsForWindparkEdit.length > 0) {
          await syncShipBackReferencesForWindparkObject(
            createdObject.doc.id,
            latestLinkedShipIdsForWindparkEdit
          );
        }
        toast.success(t('objects.objectCreatedSuccessfully'));
        allowNextNavigation();
        const createdCategory =
          typeof data.category === 'string' && data.category ? data.category : null;
        const categoryQuery = createdCategory ? `?category=${createdCategory}` : '';
        navigate(`/gmn/objects/${createdObject.doc.id}/details${categoryQuery}`);
      }
    } catch (_error) {
      toast.error(t('objects.errorSavingObject'));
    } finally {
      setIsSaving(false);
    }
  };

  const getPageTitle = () => {
    if (!isGmnUser) {
      return isEditMode ? t('objects.objectDetails') : t('objects.newObject');
    }
    return isEditMode
      ? t('objects.editObject')
      : isCopyMode
        ? t('objects.copyObject')
        : t('objects.newObject');
  };

  const getObjectLink = () => {
    const objectId = id ?? currentObject?.id;
    if (!objectId) return null;
    const routerBaseRaw = import.meta.env.VITE_APP_ROUTER_BASE || '';
    const routerBase = routerBaseRaw
      ? routerBaseRaw.startsWith('/')
        ? routerBaseRaw
        : `/${routerBaseRaw}`
      : '';
    const normalizedBase = routerBase.endsWith('/') ? routerBase.slice(0, -1) : routerBase;
    const path = `${normalizedBase}/objects/${objectId}/details`;
    if (typeof window === 'undefined') return path;
    return `${window.location.origin}${path}`;
  };

  const handleCopyLink = async () => {
    const link = getObjectLink();
    if (!link) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
        toast.success(t('objects.copyLinkSuccess'));
        return;
      }
      throw new Error('Clipboard unavailable');
    } catch (_error) {
      toast.error(t('objects.copyLinkError'));
    }
  };

  // Tab navigation logic
  const rawTab = isEditMode ? location.pathname.split('/').pop() || 'details' : 'details';
  const currentTab = rawTab === 'files' ? 'details' : rawTab;
  const basePath = isCurrentGmnDomain ? '/gmn/objects' : '/c/objects';

  const showPermissionsTab =
    isEditMode && !isViewAsActive && (isEffectiveGmnAdmin || isEffectiveCustomerAdmin);
  const tabs = [
    { value: 'details', label: t('objects.masterData'), icon: FileText },
    ...(showPermissionsTab
      ? [{ value: 'permissions', label: t('permissions.tab'), icon: ShieldCheck }]
      : []),
    { value: 'activity', label: t('common.activity'), icon: Activity },
  ];

  const renderObjectDetailsCard = () => {
    const content =
      viewMode === 'edit' ? (
        <div className="grid grid-cols-6 gap-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem className="col-span-6 md:col-span-3 lg:col-span-2">
                <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                  <FormLabel required>{t('common.name')}</FormLabel>
                  <FormControl>
                    <Input {...field} disabled={formDisabled} />
                  </FormControl>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />

          {/* Category selector - only for GMN users */}
          {isGmnUser && (
            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem className="col-span-6 md:col-span-3 lg:col-span-2">
                  <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                    <FormLabel>{t('objectCategories.category')}</FormLabel>
                    <FormControl>
                      <SearchCombobox
                        value={field.value || ''}
                        onSelect={field.onChange}
                        collectionKey="object-categories"
                        searchKey="name"
                        clearValue=""
                        disabled={formDisabled}
                        texts={{
                          selectItemMsg: t('objectCategories.selectCategory'),
                          searchPlaceholder: t('objectCategories.selectCategory'),
                          noResultsMsg: t('objectCategories.noCategory'),
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
          )}

          {/* Only show assigned customer field for GMN users not in customer view mode */}
          {isGmnUser && (
            <FormField
              control={form.control}
              name="assignedCustomer"
              render={({ field }) => (
                <FormItem className="col-span-6 md:col-span-3 lg:col-span-2">
                  <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                    <FormLabel>{t('customers.assignedCustomer')}</FormLabel>
                    <FormControl>
                      <SearchCombobox
                        value={field.value ?? undefined}
                        onSelect={field.onChange}
                        collectionKey="customers"
                        searchKey="name"
                        clearValue=""
                        disabled={formDisabled}
                        customWhere={{
                          or: [
                            { customerType: { equals: 'customer' } },
                            { customerType: { exists: false } },
                          ],
                        }}
                        texts={{
                          selectItemMsg: t('customers.selectCustomer'),
                          searchPlaceholder: t('customers.searchCustomers'),
                          noResultsMsg: t('customers.noCustomersFound'),
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
          )}

          {isGmnUser ? (
            <FormField
              control={form.control}
              name="assignedHelicopterServiceProvider"
              render={({ field }) => (
                <FormItem className="col-span-6 md:col-span-3 lg:col-span-2">
                  <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                    <FormLabel>{t('customers.assignedHelicopterServiceProvider')}</FormLabel>
                    <FormControl>
                      <SearchCombobox
                        value={field.value ?? undefined}
                        onSelect={field.onChange}
                        collectionKey="customers"
                        searchKey="name"
                        clearValue=""
                        disabled={formDisabled}
                        customWhere={{ customerType: { equals: 'heli-provider' } }}
                        texts={{
                          selectItemMsg: t('customers.selectHelicopterServiceProvider'),
                          searchPlaceholder: t('customers.searchHelicopterServiceProviders'),
                          noResultsMsg: t('customers.noHelicopterServiceProvidersFound'),
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
          ) : currentObject ? (
            <div className="col-span-6 md:col-span-3 lg:col-span-2">
              <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                <div className="text-xs font-medium text-muted-foreground">
                  {t('customers.assignedHelicopterServiceProvider')}
                </div>
                <div className="text-sm font-medium">
                  <EntityReferenceValue
                    label={
                      typeof currentObject?.assignedHelicopterServiceProvider === 'object'
                        ? currentObject?.assignedHelicopterServiceProvider?.name
                        : assignedHeliProviderForRead?.name
                    }
                    href={assignedHelicopterProviderHref}
                  />
                </div>
              </div>
            </div>
          ) : null}

          {isGmnUser && isHelicopterCategory ? (
            <FormField
              control={form.control}
              name="linkedOffshoreObjects"
              render={({ field }) => (
                <FormItem className="col-span-6 md:col-span-3 lg:col-span-2">
                  <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                    <FormLabel>{t('objects.linkedOffshoreObjects')}</FormLabel>
                    <FormControl>
                      <MultiSearchCombobox
                        value={(field.value || []) as string[]}
                        onSelect={field.onChange}
                        collectionKey="objects"
                        searchKey="name"
                        disabled={
                          formDisabled ||
                          !selectedHeliProviderId ||
                          offshoreCategoryIds.length === 0
                        }
                        customWhere={
                          selectedHeliProviderId && offshoreCategoryIds.length > 0
                            ? {
                                and: [
                                  {
                                    assignedHelicopterServiceProvider: {
                                      equals: selectedHeliProviderId,
                                    },
                                  },
                                  {
                                    category: {
                                      in: offshoreCategoryIds,
                                    },
                                  },
                                ],
                              }
                            : undefined
                        }
                        texts={{
                          selectItemMsg: t('objects.selectLinkedOffshoreObjects'),
                          searchPlaceholder: t('objects.searchLinkedOffshoreObjects'),
                          noResultsMsg: t('objects.noLinkedOffshoreObjectsFound'),
                        }}
                      />
                    </FormControl>
                    {!selectedHeliProviderId ? (
                      <p className="text-xs text-muted-foreground">
                        {t('objects.linkedOffshoreObjectsRequiresProvider')}
                      </p>
                    ) : null}
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
          ) : !isGmnUser && isHelicopterCategory ? (
            <div className="col-span-6 md:col-span-3 lg:col-span-2">
              <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                <div className="text-xs font-medium text-muted-foreground">
                  {t('objects.linkedOffshoreObjects')}
                </div>
                <div className="text-sm font-medium">
                  <EntityReferenceList items={linkedOffshoreReferenceItems} />
                </div>
              </div>
            </div>
          ) : null}

          {isGmnUser && isShipCategory ? (
            <FormField
              control={form.control}
              name="linkedWindpark"
              render={({ field }) => (
                <FormItem className="col-span-6 md:col-span-3 lg:col-span-2">
                  <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                    <FormLabel>{t('objects.linkedWindpark')}</FormLabel>
                    <FormControl>
                      <SearchCombobox
                        value={field.value ?? undefined}
                        onSelect={field.onChange}
                        collectionKey="objects"
                        searchKey="name"
                        clearValue=""
                        disabled={formDisabled || windparkCategoryIds.length === 0}
                        customWhere={
                          windparkCategoryIds.length > 0
                            ? {
                                category: {
                                  in: windparkCategoryIds,
                                },
                              }
                            : undefined
                        }
                        texts={{
                          selectItemMsg: t('objects.selectLinkedWindpark'),
                          searchPlaceholder: t('objects.searchLinkedWindpark'),
                          noResultsMsg: t('objects.noLinkedWindparkFound'),
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
          ) : isShipCategory ? (
            <div className="col-span-6 md:col-span-3 lg:col-span-2">
              <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                <div className="text-xs font-medium text-muted-foreground">
                  {t('objects.linkedWindpark')}
                </div>
                <div className="text-sm font-medium">
                  <EntityReferenceValue
                    label={linkedWindparkReferenceItem?.label}
                    href={linkedWindparkReferenceItem?.href}
                  />
                </div>
              </div>
            </div>
          ) : null}

          {canEditOffshoreLinkedHelicopters && isOffshoreCategory ? (
            <FormItem className="col-span-6 md:col-span-3 lg:col-span-2">
              <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                <FormLabel>{t('objects.linkedHelicopters')}</FormLabel>
                <FormControl>
                  <MultiSearchCombobox
                    value={linkedHelicopterIdsForOffshoreEdit}
                    onSelect={updateLinkedHelicopterIdsForOffshoreEdit}
                    collectionKey="objects"
                    searchKey="name"
                    disabled={
                      formDisabled || !selectedHeliProviderId || helicopterCategoryIds.length === 0
                    }
                    customWhere={
                      selectedHeliProviderId && helicopterCategoryIds.length > 0
                        ? {
                            and: [
                              {
                                assignedHelicopterServiceProvider: {
                                  equals: selectedHeliProviderId,
                                },
                              },
                              {
                                category: {
                                  in: helicopterCategoryIds,
                                },
                              },
                            ],
                          }
                        : undefined
                    }
                    texts={{
                      selectItemMsg: t('objects.selectLinkedHelicopters'),
                      searchPlaceholder: t('objects.searchLinkedHelicopters'),
                      noResultsMsg: t('objects.noLinkedHelicoptersFound'),
                    }}
                  />
                </FormControl>
                {!selectedHeliProviderId ? (
                  <p className="text-xs text-muted-foreground">
                    {t('objects.linkedHelicoptersRequiresProvider')}
                  </p>
                ) : null}
              </div>
            </FormItem>
          ) : isOffshoreCategory ? (
            <div className="col-span-6 md:col-span-3 lg:col-span-2">
              <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                <div className="text-xs font-medium text-muted-foreground">
                  {t('objects.linkedHelicopters')}
                </div>
                <div className="text-sm font-medium">
                  <EntityReferenceList items={linkedHelicopterReferenceItems} />
                </div>
              </div>
            </div>
          ) : null}

          {canEditWindparkLinkedShips ? (
            <FormItem className="col-span-6 md:col-span-3 lg:col-span-2">
              <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                <FormLabel>{t('objects.linkedShips')}</FormLabel>
                <FormControl>
                  <MultiSearchCombobox
                    value={linkedShipIdsForWindparkEdit}
                    onSelect={updateLinkedShipIdsForWindparkEdit}
                    collectionKey="objects"
                    searchKey="name"
                    disabled={formDisabled || shipCategoryIds.length === 0}
                    customWhere={
                      shipCategoryIds.length > 0
                        ? {
                            category: {
                              in: shipCategoryIds,
                            },
                          }
                        : undefined
                    }
                    texts={{
                      selectItemMsg: t('objects.selectLinkedShips'),
                      searchPlaceholder: t('objects.searchLinkedShips'),
                      noResultsMsg: t('objects.noLinkedShipsFound'),
                    }}
                  />
                </FormControl>
              </div>
            </FormItem>
          ) : isWindparkCategory ? (
            <div className="col-span-6 md:col-span-3 lg:col-span-2">
              <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                <div className="text-xs font-medium text-muted-foreground">
                  {t('objects.linkedShips')}
                </div>
                <div className="text-sm font-medium">
                  <EntityReferenceList items={linkedShipReferenceItems} />
                </div>
              </div>
            </div>
          ) : null}

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem className="col-span-6">
                <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                  <FormLabel>{t('common.description')}</FormLabel>
                  <FormControl>
                    <Textarea {...field} disabled={formDisabled} />
                  </FormControl>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />
        </div>
      ) : (
        <div className="grid grid-cols-6 gap-4">
          <div className="col-span-6 md:col-span-3 lg:col-span-2">
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="text-xs font-medium text-muted-foreground mb-1">
                {t('common.name')}
              </div>
              <div className="text-sm font-medium">{currentObject?.name || '-'}</div>
            </div>
          </div>
          <div className="col-span-6 md:col-span-3 lg:col-span-2">
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="text-xs font-medium text-muted-foreground mb-1">
                {t('objectCategories.category')}
              </div>
              <div className="text-sm font-medium">
                <EntityReferenceValue label={selectedCategory?.name} href={categoryHref} />
              </div>
            </div>
          </div>
          {isGmnUser && (
            <div className="col-span-6 md:col-span-3 lg:col-span-2">
              <div className="rounded-xl border border-border bg-card p-4">
                <div className="text-xs font-medium text-muted-foreground mb-1">
                  {t('customers.assignedCustomer')}
                </div>
                <div className="text-sm font-medium">
                  <EntityReferenceValue
                    label={
                      typeof currentObject?.assignedCustomer === 'object'
                        ? currentObject?.assignedCustomer?.name
                        : assignedCustomerForRead?.name
                    }
                    href={assignedCustomerHref}
                  />
                </div>
              </div>
            </div>
          )}

          {currentObject && (
            <div className="col-span-6 md:col-span-3 lg:col-span-2">
              <div className="rounded-xl border border-border bg-card p-4">
                <div className="text-xs font-medium text-muted-foreground mb-1">
                  {t('customers.assignedHelicopterServiceProvider')}
                </div>
                <div className="text-sm font-medium">
                  <EntityReferenceValue
                    label={
                      typeof currentObject?.assignedHelicopterServiceProvider === 'object'
                        ? currentObject?.assignedHelicopterServiceProvider?.name
                        : assignedHeliProviderForRead?.name
                    }
                    href={assignedHelicopterProviderHref}
                  />
                </div>
              </div>
            </div>
          )}

          {currentObject && isHelicopterCategory && (
            <div className="col-span-6 md:col-span-3 lg:col-span-2">
              <div className="rounded-xl border border-border bg-card p-4">
                <div className="text-xs font-medium text-muted-foreground mb-1">
                  {t('objects.linkedOffshoreObjects')}
                </div>
                <div className="text-sm font-medium">
                  <EntityReferenceList items={linkedOffshoreReferenceItems} />
                </div>
              </div>
            </div>
          )}

          {isOffshoreCategory && (
            <div className="col-span-6 md:col-span-3 lg:col-span-2">
              <div className="rounded-xl border border-border bg-card p-4">
                <div className="text-xs font-medium text-muted-foreground mb-1">
                  {t('objects.linkedHelicopters')}
                </div>
                <div className="text-sm font-medium">
                  <EntityReferenceList items={linkedHelicopterReferenceItems} />
                </div>
              </div>
            </div>
          )}

          {isWindparkCategory && (
            <div className="col-span-6 md:col-span-3 lg:col-span-2">
              <div className="rounded-xl border border-border bg-card p-4">
                <div className="text-xs font-medium text-muted-foreground mb-1">
                  {t('objects.linkedShips')}
                </div>
                <div className="text-sm font-medium">
                  <EntityReferenceList items={linkedShipReferenceItems} />
                </div>
              </div>
            </div>
          )}

          {currentObject && isShipCategory && (
            <div className="col-span-6 md:col-span-3 lg:col-span-2">
              <div className="rounded-xl border border-border bg-card p-4">
                <div className="text-xs font-medium text-muted-foreground mb-1">
                  {t('objects.linkedWindpark')}
                </div>
                <div className="text-sm font-medium">
                  <EntityReferenceValue
                    label={linkedWindparkReferenceItem?.label}
                    href={linkedWindparkReferenceItem?.href}
                  />
                </div>
              </div>
            </div>
          )}
          <div className="col-span-6">
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="text-xs font-medium text-muted-foreground mb-1">
                {t('common.description')}
              </div>
              <div className="text-sm text-foreground whitespace-pre-wrap wrap-break-words">
                {currentObject?.description || '-'}
              </div>
            </div>
          </div>
        </div>
      );

    // Keep master data consistent with metadata sections: section header + field cards only.
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 mb-4">
          <h4 className="text-sm font-semibold text-foreground">{t('objects.masterData')}</h4>
          <div className="h-px flex-1 bg-border" />
        </div>
        {content}
      </div>
    );
  };

  const hasCategoryMetadata =
    categoryMetadataSchema && Object.keys(categoryMetadataSchema.properties || {}).length > 0;

  const dynamicMetadataSchema = form.watch('dynamicMetadataSchema') as JsonSchemaType | undefined;
  const hasCustomMetadata =
    dynamicMetadataSchema && Object.keys(dynamicMetadataSchema.properties || {}).length > 0;

  const getOrderedFieldNames = (metadataSchema: JsonSchemaType) => {
    const fromOrder = metadataSchema.propertyOrder?.length ? metadataSchema.propertyOrder : [];
    return fromOrder.length > 0 ? fromOrder : Object.keys(metadataSchema.properties || {});
  };

  const getKnownGroupIds = (metadataSchema: JsonSchemaType) =>
    new Set((metadataSchema.groups ?? []).map((g) => g.id));

  const getFieldsForGroup = (metadataSchema: JsonSchemaType, groupId: string) => {
    const ordered = getOrderedFieldNames(metadataSchema);
    return ordered.filter((name) => {
      const fieldDef = metadataSchema.properties?.[name];
      if (fieldDef?.groupId !== groupId) return false;
      return getFieldPermission(fieldDef, effectiveGroupKey).read;
    });
  };

  const getUngroupedFields = (metadataSchema: JsonSchemaType) => {
    const ordered = getOrderedFieldNames(metadataSchema);
    const knownGroupIds = getKnownGroupIds(metadataSchema);
    return ordered.filter((name) => {
      const field = metadataSchema.properties?.[name];
      if (!field) return false;
      const fieldGroupId = field.groupId;
      if (!fieldGroupId) {
        return getFieldPermission(field, effectiveGroupKey).read;
      }
      if (knownGroupIds.has(fieldGroupId)) return false;
      return getFieldPermission(field, effectiveGroupKey).read;
    });
  };

  const categoryGroupSections = useMemo(() => {
    if (!selectedCategoryId || !hasCategoryMetadata || !categoryMetadataSchema) return [];
    const groups = [...(categoryMetadataSchema.groups ?? [])].sort((a, b) => a.order - b.order);
    const sections = groups
      .map((group) => ({
        id: `object-category-group-${group.id}`,
        label: locale === 'en-US' && group.labelEn ? group.labelEn : group.labelDe,
        fieldNames: getFieldsForGroup(categoryMetadataSchema, group.id),
      }))
      .filter((s) => s.fieldNames.length > 0);

    const ungrouped = getUngroupedFields(categoryMetadataSchema);
    if (ungrouped.length > 0) {
      sections.push({
        id: 'object-category-ungrouped',
        label: t('metadata.builder.ungrouped'),
        fieldNames: ungrouped,
      });
    }

    return sections;
  }, [
    categoryMetadataSchema,
    hasCategoryMetadata,
    locale,
    selectedCategoryId,
    t,
    effectiveGroupKey,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(categoryMetadataSchema?.groups ?? []),
  ]);

  const customGroupSections = useMemo(() => {
    if (!hasCustomMetadata || !dynamicMetadataSchema) return [];
    const groups = [...(dynamicMetadataSchema.groups ?? [])].sort((a, b) => a.order - b.order);
    const sections = groups
      .map((group) => ({
        id: `object-custom-group-${group.id}`,
        label: locale === 'en-US' && group.labelEn ? group.labelEn : group.labelDe,
        fieldNames: getFieldsForGroup(dynamicMetadataSchema, group.id),
      }))
      .filter((s) => s.fieldNames.length > 0);

    const ungrouped = getUngroupedFields(dynamicMetadataSchema);
    if (ungrouped.length > 0) {
      sections.push({
        id: 'object-custom-ungrouped',
        label: t('metadata.builder.ungrouped'),
        fieldNames: ungrouped,
      });
    }

    return sections;
  }, [
    dynamicMetadataSchema,
    hasCustomMetadata,
    locale,
    t,
    effectiveGroupKey,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(dynamicMetadataSchema?.groups ?? []),
  ]);

  const categoryMetadataValuesWatch = useWatch({
    control: form.control,
    name: 'categoryMetadataValues',
  }) as Record<string, unknown> | undefined;

  const dynamicMetadataValuesWatch = useWatch({
    control: form.control,
    name: 'dynamicMetadataValues',
  }) as Record<string, unknown> | undefined;

  const getGroupStatusIndicator = useCallback(
    (
      metadataSchema: JsonSchemaType | undefined,
      fieldNames: string[],
      values: Record<string, unknown> | undefined
    ): {
      statusValue: StatusValue | undefined;
      statusOptions?: StatusOption[];
      icon?: string;
    } | null => {
      if (!metadataSchema?.properties) return null;

      for (const fieldName of fieldNames) {
        const fieldDef = metadataSchema.properties[fieldName] as
          | (Record<string, unknown> & { type?: string; isGroupIndicator?: boolean })
          | undefined;
        if (!fieldDef) continue;

        if (fieldDef.type === 'status' && fieldDef.isGroupIndicator === true) {
          const statusOptions =
            ensureStatusOptionsWithIds(
              (fieldDef.statusOptions as StatusOption[] | undefined) ?? []
            ) ?? [];
          const icon = fieldDef.icon as string | undefined;
          const statusValue = values?.[fieldName] as StatusValue | undefined;
          return { statusValue, statusOptions, icon };
        }
      }

      return null;
    },
    []
  );

  const objectFiles = useMemo(
    () => (Array.isArray(currentObject?.files) ? currentObject?.files : []),
    [currentObject?.files]
  );
  const canManageFiles = isEffectiveGmnAdmin;
  const canEditFiles = canManageFiles && viewMode === 'edit' && !isViewAsActive;
  const canShowFileControls = canManageFiles && (viewMode === 'edit' || isViewAsActive);
  const showFilesSection =
    !!id && (viewMode === 'edit' || objectFiles.length > 0 || canShowFileControls);

  const sidemenuItems = useMemo(() => {
    const items: Array<{
      id: string;
      label: string;
      kind: 'master-data' | 'files' | 'category-group' | 'custom-group';
      statusIndicator?: {
        statusValue: StatusValue | undefined;
        statusOptions?: StatusOption[];
        icon?: string;
      } | null;
    }> = [
      {
        id: 'object-master-data',
        label: t('objects.masterData'),
        kind: 'master-data',
      },
    ];

    if (showFilesSection) {
      items.push({
        id: 'object-files',
        label: t('objects.files.title'),
        kind: 'files',
      });
    }

    for (const section of categoryGroupSections) {
      const statusIndicator = getGroupStatusIndicator(
        categoryMetadataSchema,
        section.fieldNames,
        categoryMetadataValuesWatch
      );
      items.push({
        id: section.id,
        label: section.label,
        kind: 'category-group',
        statusIndicator,
      });
    }
    for (const section of customGroupSections) {
      const statusIndicator = getGroupStatusIndicator(
        dynamicMetadataSchema,
        section.fieldNames,
        dynamicMetadataValuesWatch
      );
      items.push({
        id: section.id,
        label: section.label,
        kind: 'custom-group',
        statusIndicator,
      });
    }

    return items;
  }, [
    categoryGroupSections,
    categoryMetadataSchema,
    categoryMetadataValuesWatch,
    customGroupSections,
    dynamicMetadataSchema,
    dynamicMetadataValuesWatch,
    getGroupStatusIndicator,
    showFilesSection,
    t,
  ]);

  const activeSectionId = useScrollSpy(
    sidemenuItems.map((i) => i.id),
    {
      root: scrollRoot,
      rootMargin: '-15% 0px -75% 0px',
    }
  );

  const scrollToSection = (id: string) => {
    scrollToTarget(id, { root: scrollRoot });
  };

  const renderMetadataSections = ({
    schema,
    metadataSchema,
    valuesPath,
    sections,
  }: {
    schema: z.ZodObject<any>;
    metadataSchema: JsonSchemaType;
    valuesPath: string[];
    sections: Array<{ id: string; label: string; fieldNames: string[] }>;
  }) => {
    if (sections.length === 0) {
      return <div className="text-sm text-muted-foreground">({t('metadata.noMetadata')})</div>;
    }

    return (
      <div className="space-y-10">
        {sections.map((section) => (
          <div key={section.id}>
            <div id={section.id} className="scroll-mt-28">
              <div className="flex items-center gap-3 mb-4">
                <h4 className="text-sm font-semibold text-foreground">{section.label}</h4>
                <div className="h-px flex-1 bg-border" />
              </div>
            </div>
            <MetadataAutoForm
              schema={pickZodObjectFields(schema, section.fieldNames)}
              form={form}
              path={valuesPath}
              metadataSchema={metadataSchema}
              groupKey={effectiveGroupKey}
              disabled={formDisabled}
              disableGrouping={true}
              currentObject={currentObject as unknown as PortalViewObject}
              canReadObjects={canReadObjects}
              domain={effectiveDomain}
            />
          </div>
        ))}
      </div>
    );
  };

  const categoryMetadataValues = useMemo(
    () =>
      parseMetadata(currentObject?.categoryMetadataValues as Record<string, unknown> | undefined),
    [currentObject?.categoryMetadataValues]
  );
  const customMetadataValues = useMemo(
    () =>
      parseMetadata(currentObject?.dynamicMetadataValues as Record<string, unknown> | undefined),
    [currentObject?.dynamicMetadataValues]
  );

  const renderMetadataReadSections = ({
    metadataSchema,
    values,
    sections,
  }: {
    metadataSchema: JsonSchemaType;
    values: Record<string, unknown> | undefined;
    sections: Array<{ id: string; label: string; fieldNames: string[] }>;
  }) => {
    if (sections.length === 0) {
      return <div className="text-sm text-muted-foreground">({t('metadata.noMetadata')})</div>;
    }

    return (
      <div className="space-y-10">
        {sections.map((section) => (
          <div key={section.id}>
            <div id={section.id} className="scroll-mt-28">
              <div className="flex items-center gap-3 mb-4">
                <h4 className="text-sm font-semibold text-foreground">{section.label}</h4>
                <div className="h-px flex-1 bg-border" />
              </div>
            </div>
            <MetadataReadView
              metadataSchema={metadataSchema}
              values={values}
              fieldNames={section.fieldNames}
              groupKey={effectiveGroupKey}
              currentObject={currentObject as unknown as PortalViewObject}
              canReadObjects={canReadObjects}
              domain={effectiveDomain}
            />
          </div>
        ))}
      </div>
    );
  };

  const renderCategoryMetadataCard = () => {
    if (!selectedCategoryId || !hasCategoryMetadata || !categoryMetadataSchema) return null;

    return viewMode === 'edit' ? (
      <div>
        {renderMetadataSections({
          schema: categoryMetadataZodSchema,
          metadataSchema: categoryMetadataSchema,
          valuesPath: ['categoryMetadataValues'],
          sections: categoryGroupSections,
        })}
      </div>
    ) : (
      <div>
        {renderMetadataReadSections({
          metadataSchema: categoryMetadataSchema,
          values: categoryMetadataValues,
          sections: categoryGroupSections,
        })}
      </div>
    );
  };

  const renderCustomMetadataCard = () => {
    const showEmptyState = viewMode === 'edit' && isEffectiveGmnAdmin && !isViewAsActive;
    if (!showEmptyState && (!hasCustomMetadata || !dynamicMetadataSchema)) return null;

    return (
      <div>
        <div id="object-custom-metadata" className="scroll-mt-28">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <Database className="size-4 text-muted-foreground" />
              <h4 className="text-sm font-semibold text-foreground">
                {t('metadata.customMetadata')}
              </h4>
            </div>
            {isEffectiveGmnAdmin && viewMode === 'edit' && !isViewAsActive && (
              <Button variant="outline" size="sm" onClick={openMetadataDrawer} type="button">
                <Edit3 className="size-3!" />
                {t('metadata.addCustomMetadata')}
              </Button>
            )}
          </div>
        </div>

        {viewMode === 'edit' ? (
          hasCustomMetadata && dynamicMetadataSchema ? (
            renderMetadataSections({
              schema: dynamicMetadataZodSchema,
              metadataSchema: dynamicMetadataSchema,
              valuesPath: ['dynamicMetadataValues'],
              sections: customGroupSections,
            })
          ) : (
            <div className="text-sm text-muted-foreground">({t('metadata.noMetadata')})</div>
          )
        ) : hasCustomMetadata && dynamicMetadataSchema ? (
          renderMetadataReadSections({
            metadataSchema: dynamicMetadataSchema,
            values: customMetadataValues,
            sections: customGroupSections,
          })
        ) : null}
      </div>
    );
  };

  const renderContent = () => {
    switch (currentTab) {
      case 'details':
        return (
          <div className="grid gap-6">
            <Form {...form}>
              <div className="grid gap-6 lg:grid-cols-12">
                <div className="lg:col-span-3">
                  {/* Mobile: jump select */}
                  <div className="lg:hidden">
                    <Select
                      value={activeSectionId ?? sidemenuItems[0]?.id ?? 'object-master-data'}
                      onValueChange={(id) => scrollToSection(id)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {sidemenuItems.map((item) => (
                          <SelectItem key={item.id} value={item.id}>
                            {item.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Desktop: sticky sidemenu */}
                  <div className="hidden lg:block sticky top-4 self-start max-h-[calc(100vh-2rem)] overflow-auto">
                    <ObjectMetadataSidemenu
                      items={sidemenuItems}
                      activeId={activeSectionId}
                      onSelect={scrollToSection}
                      showCustomSeparator={customGroupSections.length > 0}
                    />
                  </div>
                </div>

                <div className="lg:col-span-9 grid gap-6">
                  <div id="object-master-data" className="scroll-mt-28">
                    {renderObjectDetailsCard()}
                  </div>

                  {showFilesSection && (
                    <div id="object-files" className="scroll-mt-28">
                      <ObjectFiles
                        objectId={id}
                        object={currentObject}
                        canManage={canShowFileControls}
                        readOnly={!canEditFiles}
                        variant={viewMode === 'edit' ? 'full' : 'compact'}
                      />
                    </div>
                  )}

                  {renderCategoryMetadataCard()}

                  {/* Minimal separator between category + custom metadata in the main column */}
                  {hasCategoryMetadata && hasCustomMetadata && (
                    <div className="h-px bg-border my-1" aria-hidden="true" />
                  )}

                  {renderCustomMetadataCard()}
                </div>
              </div>
            </Form>
          </div>
        );
      case 'activity':
        return <ObjectActivity objectId={id} groupKey={effectiveGroupKey} />;
      case 'files':
        return <ObjectFiles objectId={id} object={currentObject} canManage={false} readOnly />;
      case 'permissions':
        if (!showPermissionsTab || !id) {
          return (
            <div className="text-sm text-muted-foreground">{t('permissions.notAuthorized')}</div>
          );
        }
        return (
          <EntityPermissionsTab
            entityId={id}
            entityType="objects"
            customerContexts={prioritizedPermissionCustomerContexts}
          />
        );
      default:
        return null;
    }
  };

  const showHeaderActions = currentTab === 'details';
  const headerTitle = isEditMode && currentObject ? currentObject.name : getPageTitle();

  const assignedCustomerName =
    typeof currentObject?.assignedCustomer === 'object'
      ? currentObject?.assignedCustomer?.name
      : assignedCustomerForRead?.name;
  const assignedHeliProviderName =
    typeof currentObject?.assignedHelicopterServiceProvider === 'object'
      ? currentObject?.assignedHelicopterServiceProvider?.name
      : assignedHeliProviderForRead?.name;
  const assignedCustomerHref = getCustomerDetailHref({
    customerId: assignedCustomerId,
    customerType: 'customer',
    domain: effectiveDomain,
    canReadCustomers,
    effectiveUserCustomerId,
  });
  const assignedHelicopterProviderHref = getCustomerDetailHref({
    customerId: assignedHelicopterServiceProviderId,
    customerType: 'heli-provider',
    domain: effectiveDomain,
    canReadCustomers,
    effectiveUserCustomerId,
  });
  const categoryHref = getObjectCategoryDetailHref({
    categoryId: selectedCategory?.id,
    isGmnAdmin: isEffectiveGmnAdmin,
  });

  const viewAsUserLabel = useMemo(() => {
    if (!viewAsUser) return '';
    const fullName = `${viewAsUser.firstName ?? ''} ${viewAsUser.lastName ?? ''}`.trim();
    return fullName !== '' ? fullName : viewAsUser.email || viewAsUser.id;
  }, [viewAsUser]);

  const viewAsControls = canUseViewAs ? (
    <div className="flex flex-wrap items-center gap-2">
      {isViewAsActive && viewAsUserLabel ? (
        <Badge variant="outline-solid" data-testid="view-as-indicator">
          <Eye size={14} className="mr-1" />
          {t('permissions.viewAs.activeLabel', { name: viewAsUserLabel })}
        </Badge>
      ) : null}
      {!isViewAsActive ? (
        <Button
          type="button"
          variant="outline"
          onClick={() => setIsViewAsDialogOpen(true)}
          data-testid="view-as-open"
        >
          <Eye size={16} className="mr-2" />
          {t('permissions.viewAs.label')}
        </Button>
      ) : null}
      {isViewAsActive ? (
        <Button type="button" variant="outline" onClick={clearViewAs} data-testid="view-as-exit">
          <X size={16} className="mr-2" />
          {t('permissions.viewAs.exit')}
        </Button>
      ) : null}
    </div>
  ) : null;

  const metaItems =
    isEditMode && currentObject
      ? [
          ...(isGmnUser && currentObject.assignedCustomer
            ? [
                {
                  icon: Users,
                  label:
                    assignedCustomerName && assignedCustomerName.length > 0
                      ? assignedCustomerName
                      : '-',
                  href: assignedCustomerHref ?? undefined,
                },
              ]
            : []),
          ...(currentObject.assignedHelicopterServiceProvider
            ? [
                {
                  icon: Users,
                  label:
                    assignedHeliProviderName && assignedHeliProviderName.length > 0
                      ? t('customers.helicopterServiceProviderLabel', {
                          name: assignedHeliProviderName,
                        })
                      : t('customers.helicopterServiceProviderLabel', { name: '-' }),
                  href: assignedHelicopterProviderHref ?? undefined,
                },
              ]
            : []),
          ...(selectedCategory?.name
            ? [
                {
                  icon: getObjectCategoryIcon(selectedCategory.icon),
                  label: selectedCategory.name,
                  href: categoryHref ?? undefined,
                },
              ]
            : []),
        ]
      : undefined;

  const headerIcon = selectedCategory ? getObjectCategoryIcon(selectedCategory.icon) : Box;

  return (
    <DetailPageLayout
      containerProps={{ animation: 'slide-right' }}
      headerProps={{
        backHref,
        backLabel: t('objects.backToOverview'),
        title: headerTitle,
        icon: headerIcon,
        isCreateMode: !isEditMode,
        metaItems,
        subtitle: !isEditMode ? t('objects.createNewObjectHint') : undefined,
        actions:
          showHeaderActions && isEditMode && (viewMode === 'read' || isViewAsActive) ? (
            <div className="flex flex-wrap items-center gap-3">
              {viewAsControls}
              {viewMode === 'read' && !isViewAsActive ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void handleCopyLink()}
                  data-testid="object-copy-link"
                >
                  <Link2 size={16} className="mr-2" />
                  {t('objects.copyLink')}
                </Button>
              ) : null}
              {viewMode === 'read' && canSaveObject ? (
                <Button
                  type="button"
                  onClick={() => setViewMode('edit')}
                  data-testid="object-enter-edit"
                >
                  <Edit3 size={16} className="mr-2" />
                  {t('common.edit')}
                </Button>
              ) : null}
            </div>
          ) : undefined,
        secondaryAction:
          showHeaderActions && !isViewAsActive
            ? isEditMode
              ? viewMode === 'edit'
                ? {
                    label: t('common.discard'),
                    onClick: discardChanges,
                  }
                : undefined
              : {
                  label: t('common.discard'),
                  onClick: () => navigate(backHref),
                }
            : undefined,
        primaryAction:
          showHeaderActions && canSaveObject && (isEditMode ? viewMode === 'edit' : true)
            ? {
                label: isSaving
                  ? isEditMode
                    ? t('common.updating')
                    : t('common.creating')
                  : isEditMode
                    ? t('common.update')
                    : t('common.create'),
                onClick: () => void form.handleSubmit(handleSubmit)(),
                disabled: isSaving || !canSaveObject,
                loading: isSaving,
              }
            : undefined,
      }}
    >
      {isEditMode && <TabNav tabs={tabs} basePath={`${basePath}/${id}`} variant="underline" />}

      <form id="object-form" onSubmit={form.handleSubmit(handleSubmit)}>
        {renderContent()}
      </form>

      {isEffectiveGmnAdmin && !isViewAsActive && (
        <Dialog open={isMetadataDrawerOpen} onOpenChange={setIsMetadataDrawerOpen}>
          <DialogContent
            className="max-w-[90vw]! w-[1400px]! max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0"
            showCloseButton={false}
          >
            <div className="flex-1 overflow-hidden">
              <div className="h-[70vh]">
                <MetadataBuilderWithPreview
                  value={drawerSchema}
                  onChange={setDrawerSchema}
                  userGroups={userGroups}
                  header={false}
                  storageKey="gmn.metadataBuilder.objectDrawer.formPreview.open"
                />
              </div>
            </div>
            <DialogFooter className="border-t p-5">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsMetadataDrawerOpen(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button type="button" onClick={handleMetadataBuilderSave}>
                {t('common.save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {canUseViewAs && isEditMode && id ? (
        <ViewAsDialog
          open={isViewAsDialogOpen}
          onOpenChange={setIsViewAsDialogOpen}
          entityType="objects"
          entityId={id}
          customerContexts={prioritizedPermissionCustomerContexts}
          selectedUserId={viewAsUser?.id ?? null}
          onSelectUser={(user) => {
            setViewAsUserId(user.id);
            setIsViewAsDialogOpen(false);
          }}
        />
      ) : null}

      {unsavedDialog}
    </DetailPageLayout>
  );
}
